import type { TOrgInfo } from "@/types";
import { useAppDispatch } from "@store";
import { setDialogId } from "@store/global-slice";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useCurrentAccount } from "../providers/current-account-provider";
import type { RegularSettingsType } from "../types";

const keys: (keyof RegularSettingsType)[] = [
  "hidePoweredByStorydoc",
  "passwordEnabled",
  "importPdf",
  "showAnalyticsPlaceholder",
  "title",
  "emailDomain",
  "allowedIntegrations",
  "autoGenerateGif",
  "trialEnd",
  "optimizeVideoEnabled",
  "customSubdomainEnabled",
  "whitelabelDomainEnabled",
  "downloadablePdf",
  "apiCreditsLimit",
  "emailSendsLimit",
  "sendingDomainEnabled",
  "isAiImageFeatureDisabled",
  "isScrollBySlideEnabled",
  "isCreatorLiteCrossEditEnabled",
  "cancelAt",
  "importPdfV2",
  "onboardingEnabled",
];

export const useRegularDefaultState = () => {
  const dispatch = useAppDispatch();
  const { account, isLoading, isError } = useCurrentAccount();

  const { register, setValue, reset, control, watch, handleSubmit } =
    useForm<RegularSettingsType>();

  useEffect(() => {
    if (account) {
      const defaultValues = keys.reduce((acc: Record<string, unknown>, key) => {
        acc[key] = account[key as keyof TOrgInfo];
        return acc;
      }, {});
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (isError) {
      dispatch(setDialogId({ id: null }));
      toast.error("Failed to get organization settings");
    }
  }, [isError, dispatch]);

  return {
    register,
    setValue,
    reset,
    control,
    watch,
    handleSubmit,
    isLoading,
    account,
  };
};
